import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import * as Store from '@apply/store';
import { Footer } from '../common_v2/components/Footer';
import { alertModal } from '@apply/ui';
import { ChangePasswordResponse } from '@apply/store/src/cross';
import { isMobileScreen } from '@apply/ui';
import { Panel, PanelTheme, PanelBorderRadius, TextBox, Button, ButtonTheme } from '@apply/components';

export interface Actions {
    resetPassword?: (id: string, resetToken: string, password: string) => Promise<ChangePasswordResponse>;
}
export interface ResetPasswordPageV2Props {
    actions?: Actions;
}

export const ResetPasswordPageV2: React.FC<ResetPasswordPageV2Props> = ({ actions }) => {
    const navigate = useNavigate();
    const mobileScreen = isMobileScreen();
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const id = params.get('id');
    const code = params.get('code');

    const [submitting, setSubmitting] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string>('');
    const [matchingError, setMatchingError] = useState<boolean>(false);
    const [passwordResetResult, setPasswordResetResult] = useState<ChangePasswordResponse>(null);
    const [successful, setSuccessful] = useState<boolean>(false);
    const mounted = useRef<boolean>(true);

    const [password, setPassword] = useState<string>('');
    const [passwordConfirm, setPasswordConfirm] = useState<string>('');

    const ChangePasswordValidationMessage = {
        passwordNotMatching: 'Passwords do not match',
        passwordTooShort: 'At least 8 characters',
        passwordRequiresMixedCase: 'A mixture of both uppercase and lowercase letters',
        passwordRequiresDigit: 'A mixture of letters and numbers',
        passwordRequiresSpecialCharacter: 'Inclusion of at least one special character, e.g., ! @ # ?',
    };

    useEffect(() => {
        setMatchingError(false);
        setPasswordResetResult(null);

        if (!password || !passwordConfirm) {
            return;
        }

        if (password && passwordConfirm && password !== passwordConfirm) {
            setMatchingError(true);
            return;
        }
    }, [password, passwordConfirm]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const handlePasswordReset = async () => {
        setSubmitting(true);

        try {
            //why
            const result = await actions.resetPassword(id, code, password);
            if (mounted) {
                setSubmitting(false);

                if (result) {
                    if (result.errors && result.errors.length > 0) {
                        // Some other error in the errors collection delegate it to catch.
                        throw new Error(result.errors.join('; '));
                    }

                    if (result.result) {
                        if (result && result.result && result.result.succeeded) {
                            setSuccessful(true);
                        } else {
                            const {
                                passwordTooShort,
                                passwordRequiresMixedCase,
                                passwordRequiresDigit,
                                passwordRequiresSpecialCharacter,
                            } = result.result;
                            if (
                                passwordTooShort ||
                                passwordRequiresMixedCase ||
                                passwordRequiresDigit ||
                                passwordRequiresSpecialCharacter
                            ) {
                                setPasswordResetResult(result);
                            }
                        }
                    } else {
                        throw new Error('An unexpected error occurred and your password could not be reset. Please try again.');
                    }
                } else {
                    throw new Error('An unexpected error occurred and your password could not be reset. Please try again.');
                }
            }
        } catch (err) {
            // istanbul ignore else
            if (mounted) {
                //Error message could contain multiple errors seperated by ;. If so
                //put into a <ul><li>... and show all the errors ???
                setSubmitting(false);
                setSubmitError(err.message);
            }
        }
    };

    useEffect(() => {
        if (submitError) {
            alertModal({
                message: 'An error has occurred while communicating with our server.  Please try your operation again.',
            });
            setSubmitError(undefined);
        }
    }, [submitError]);

    if (successful) {
        return navigate('/login');
    }

    const renderChangePasswordContent = () => {
        return (
            <>
                <main>
                    <h2>Create New Password</h2>
                    <div className='changePassword subheader'>
                        Enter your new password. It must meet the following criteria:
                        <br />
                        <br />
                        <ul>
                            <li>{ChangePasswordValidationMessage.passwordTooShort}</li>
                            <li>{ChangePasswordValidationMessage.passwordRequiresMixedCase}</li>
                            <li>{ChangePasswordValidationMessage.passwordRequiresDigit}</li>
                            <li>{ChangePasswordValidationMessage.passwordRequiresSpecialCharacter}</li>
                        </ul>
                    </div>
                    <TextBox
                        id='password'
                        dataTestid='resetpassword-password'
                        type='password'
                        value={password}
                        onChange={setPassword}
                        label='Password'
                        name='password'
                        required={true}
                    />
                    <TextBox
                        id='confirmPassword'
                        dataTestid='resetpassword-confirm-password'
                        type='password'
                        value={passwordConfirm}
                        onChange={setPasswordConfirm}
                        label='Confirm Password'
                        name='confirmPassword'
                        required={true}
                    />
                    {(matchingError || (passwordResetResult && passwordResetResult.result)) && (
                        <div className='changePassword validationError'>
                            Your password failed to meet the requirements indicated below:
                            <ul>
                                {matchingError && <li>{ChangePasswordValidationMessage.passwordNotMatching}</li>}
                                {passwordResetResult?.result.passwordTooShort && (
                                    <li>{ChangePasswordValidationMessage.passwordTooShort}</li>
                                )}
                                {passwordResetResult?.result.passwordRequiresMixedCase && (
                                    <li>{ChangePasswordValidationMessage.passwordRequiresMixedCase}</li>
                                )}
                                {passwordResetResult?.result.passwordRequiresDigit && (
                                    <li>{ChangePasswordValidationMessage.passwordRequiresDigit}</li>
                                )}
                                {passwordResetResult?.result.passwordRequiresSpecialCharacter && (
                                    <li>{ChangePasswordValidationMessage.passwordRequiresSpecialCharacter}</li>
                                )}
                            </ul>
                        </div>
                    )}
                    <div className='changePassword primaryActions'>
                        <Button
                            id='resetpassword-button'
                            dataTestid='resetpassword-button'
                            key='resetpassword-button'
                            width='80%'
                            theme={ButtonTheme.Primary}
                            disabled={submitting || matchingError || !password || !passwordConfirm}
                            handleClick={handlePasswordReset}
                        >
                            Create Password
                        </Button>
                    </div>
                    <div className='changePassword secondaryActions'>
                        <div style={{ width: '100%' }}>
                            <Button
                                id='login-button'
                                dataTestid='navigate-login-button'
                                key='login-button'
                                theme={ButtonTheme.LinkBlue}
                                handleClick={(e) => navigate('/login')}
                                width='100px'
                            >
                                Log In
                            </Button>
                        </div>
                        <div style={{ float: 'right', width: 'auto' }}>
                            <Button
                                id='register-button'
                                dataTestid='navigate-register-button'
                                key='register-button'
                                theme={ButtonTheme.LinkBlue}
                                handleClick={(e) => navigate('/register')}
                                width='100px'
                            >
                                Register
                            </Button>
                        </div>
                    </div>
                </main>
            </>
        );
    };

    return (
        <>
            <div className='app-container'>
                <div className='app-container authenticationContainer'>
                    {!mobileScreen ? (
                        <div className='authenticationContainer-desktop'>
                            <Panel
                                theme={PanelTheme.White}
                                borderRadius={PanelBorderRadius.Medium}
                                width='90%'
                            >
                                <div className='authenticationContainer-desktop-desktopWrapper'>
                                    {renderChangePasswordContent()}
                                </div>
                            </Panel>
                        </div>
                    ) : (
                        <div className='authenticationContainer mobile'>{renderChangePasswordContent()}</div>
                    )}
                </div>
            </div>
            <Footer />
        </>
    );
};

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            resetPassword: (id: string, resetToken: string, password: string) =>
                dispatch(Store.CrossStore.resetPassword(id, resetToken, password)),
        },
        dispatch,
    };
    return props;
};

export default connect(null, mapDispatchToProps)(ResetPasswordPageV2);
